import { Element } from "@xmpp/xml"
import parse from "@xmpp/xml/lib/parse"
import type { AppStore } from "./reducers/store"
import { Client, xml } from "@xmpp/client"
import { getItem } from "./reducers/sendoutQueueSelectors"
import {
  markFailed,
  markSent,
  SendoutQueueItem,
} from "./reducers/sendoutQueueSlice"
import { fetchArchivePage } from "./api/xeps/MAM"
import RoomSyncQueue from "./lib/roomSyncQueue"
import PendingMessageRegistry from "./lib/PendingMessageRegistry"
import { isAfter, addSeconds } from "date-fns"

type SendoutQueueWorkerType = {
  send: (messageId: string) => void
}

export const getAckGroupchatId = (stanza: Element): string | undefined => {
  if (!stanza.is("message")) return
  if (stanza.attrs.type === "groupchat") {
    return stanza.attrs.id
  }

  // MAM response
  const mamMessage = stanza
    ?.getChild("result", "urn:xmpp:mam:2")
    ?.getChild("forwarded", "urn:xmpp:forward:0")
    ?.getChild("message")
  if (mamMessage?.attrs?.type === "groupchat") {
    return mamMessage.attrs.id
  }

  return undefined
}

const getLastMessages = async (
  client: Client,
  roomJid: string,
  n: number,
  timeout: number,
): Promise<Element[]> => {
  const result = await fetchArchivePage(
    client,
    {
      toJid: roomJid,
      before: true,
      max: n.toString(),
    },
    timeout,
  )
  return result.accumulator
}

async function raceWithTimeout<T>(
  promise: Promise<T>,
  ms: number,
): Promise<T | "timeout"> {
  const timeout = new Promise<"timeout">((resolve) =>
    setTimeout(() => resolve("timeout"), ms),
  )
  return Promise.race([promise, timeout])
}

function backoff(attempt: number): Promise<void> {
  const delay = Math.min(1000 * 2 ** (attempt - 1), 10_000)
  return new Promise((resolve) => setTimeout(resolve, delay))
}

export type SendoutQueueWorkerConfig = {
  waitTimeout?: number
  archiveScanCount?: number
  maxRetries?: number
  archiveTimeout?: number
}

type AttemptResult =
  | { outcome: "delivered" }
  | { outcome: "retry"; shouldSendNext: boolean }

const SendoutQueueWorker = (
  store: AppStore,
  xmpp: Client,
  config: SendoutQueueWorkerConfig = {},
): SendoutQueueWorkerType => {
  const {
    waitTimeout: WAIT_TIMEOUT = 5_000,
    archiveScanCount: ARCHIVE_SCAN_COUNT = 50,
    maxRetries: MAX_RETRIES = 20,
    archiveTimeout: ARCHIVE_TIMEOUT = 5_000,
  } = config

  const registry = new PendingMessageRegistry()

  xmpp.on("stanza", (stanza: Element) => {
    const ackId = getAckGroupchatId(stanza)
    if (ackId) {
      registry.resolve(ackId, "delivered")
    }

    if (stanza.is("message") && stanza.attrs.type === "error") {
      const id = stanza.attrs.id
      if (id) {
        registry.resolve(id, "rejected")
      }
    }
  })

  const MAX_SEND_DELAY = 60 * 10

  const tooOldItem = (item: SendoutQueueItem): boolean => {
    if (!item.addedAt) {
      return false
    }

    return isAfter(
      new Date(),
      addSeconds(new Date(item.addedAt), MAX_SEND_DELAY),
    )
  }

  const DELAYED_DELIVERY_THRESHOLD = 30

  const attachDelay = (stanza: Element, item: SendoutQueueItem) => {
    if (!item.addedAt) return
    if (
      !isAfter(
        new Date(),
        addSeconds(new Date(item.addedAt), DELAYED_DELIVERY_THRESHOLD),
      )
    )
      return

    stanza.append(
      xml("delay", { xmlns: "urn:xmpp:delay", stamp: item.addedAt }),
    )
  }

  const rejoinRoom = async (roomJid: string, attempt: number) => {
    console.log(
      `[SendoutQueueWorker] Rejoining room ${roomJid} before attempt ${attempt}`,
    )
    try {
      await window.mucPlugin.joinRoom(
        roomJid,
        window.myJID.local,
        new Date(),
        WAIT_TIMEOUT,
      )
    } catch (error) {
      console.log(
        `[SendoutQueueWorker] Rejoin failed for ${roomJid}, attempt ${attempt}`,
        error,
      )
    }
  }

  const tryOnce = async (
    messageId: string,
    stanza: Element,
    item: SendoutQueueItem,
    attempt: number,
    shouldSend: boolean,
    deliveryPromise: Promise<"delivered" | "rejected">,
  ): Promise<AttemptResult> => {
    RoomSyncQueue.prioritize(item.targetJid)

    if (shouldSend) {
      if (attempt > 6) {
        await rejoinRoom(item.targetJid, attempt)
      }

      console.log(
        `[SendoutQueueWorker] Sending ${messageId}, attempt ${attempt}`,
      )
      // `globalThis.` jest tu nośne, nie ozdobne: `window?.foo` NIE chroni przed
      // niezadeklarowaną nazwą — optional chaining zwiera się tylko na wartości
      // null/undefined, więc samo odczytanie `window` rzuca ReferenceError, gdy
      // globala nie ma. Ten worker dobiega do końca w promise'ach, których nikt
      // nie awaitował, więc w testach kontynuacja ląduje już po zwinięciu jsdom
      // (bez `window`) i wywracała cały build jako unhandled rejection. Odczyt
      // właściwości na globalThis nigdy nie rzuca. Drugie `?.` osłania samo
      // `analytics`, którego brakuje, gdy Segment się nie wczytał.
      globalThis.window?.analytics?.track("Sendout.Attempt", {
        messageId,
        attempt,
      })
      try {
        await xmpp.send(stanza)
      } catch (error) {
        console.log(
          `[SendoutQueueWorker] Send failed for ${messageId}, attempt ${attempt}`,
          error,
        )
        return { outcome: "retry", shouldSendNext: true }
      }
    } else {
      console.log(
        `[SendoutQueueWorker] Skipping send for ${messageId}, attempt ${attempt} (archive fetch failed previously)`,
      )
    }

    const result = await raceWithTimeout(deliveryPromise, WAIT_TIMEOUT)

    if (result === "delivered") {
      return { outcome: "delivered" }
    }

    if (result === "rejected") {
      console.log(
        `[SendoutQueueWorker] Rejected ${messageId}, attempt ${attempt} — will retry`,
      )
      globalThis.window?.analytics?.track("Sendout.Rejected", {
        messageId,
        attempt,
      })
    }

    // Timeout or rejected — check archive
    try {
      const archived = await getLastMessages(
        xmpp,
        item.targetJid,
        ARCHIVE_SCAN_COUNT,
        ARCHIVE_TIMEOUT,
      )
      const found = archived.some((s) => getAckGroupchatId(s) === messageId)
      if (found) {
        console.log(
          `[SendoutQueueWorker] Found ${messageId} in archive, attempt ${attempt}`,
        )
        globalThis.window?.analytics?.track("Sendout.FoundInArchive", {
          messageId,
          attempt,
        })
        return { outcome: "delivered" }
      }
      return { outcome: "retry", shouldSendNext: true } // Positively not there — safe to resend
    } catch (error) {
      console.log(
        `[SendoutQueueWorker] Archive fetch failed for ${messageId}, attempt ${attempt}`,
        error,
      )
      globalThis.window?.analytics?.track("Sendout.ArchiveFetchFailed", {
        messageId,
        attempt,
      })
      return { outcome: "retry", shouldSendNext: false } // Don't know — don't resend, just retry archive
    }
  }

  const trySendout = async (messageId: string) => {
    const item = getItem(messageId)(store.getState().sendoutQueue)

    if (!item) return
    if (item.messageType !== "groupchat") return

    if (tooOldItem(item)) {
      globalThis.window?.analytics?.track("Sendout.Failed", {
        messageId,
        reason: "tooOld",
      })
      store.dispatch(markFailed({ messageId }))
      return
    }

    const stanza = parse(item.stanza)
    attachDelay(stanza, item)
    delete stanza.attrs.from

    const deliveryPromise = registry.register(messageId, item.targetJid)
    let shouldSend = true

    try {
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        const attemptResult: AttemptResult = await tryOnce(
          messageId,
          stanza,
          item,
          attempt,
          shouldSend,
          deliveryPromise,
        )

        if (attemptResult.outcome === "delivered") {
          console.log(
            `[SendoutQueueWorker] Delivered ${messageId}, attempt ${attempt}`,
          )
          registry.resolve(messageId, "delivered")
          globalThis.window?.analytics?.track("Sendout.Sent", {
            messageId,
            attempt,
          })
          store.dispatch(markSent({ messageIds: [messageId] }))
          globalThis.window?.persistor?.flush()
          return
        }

        shouldSend = attemptResult.shouldSendNext
        await backoff(attempt)
      }

      console.warn(`[SendoutQueueWorker] Exhausted retries for ${messageId}`)
      globalThis.window?.analytics?.track("Sendout.Failed", {
        messageId,
        reason: "exhausted",
      })
      store.dispatch(markFailed({ messageId }))
    } finally {
      registry.delete(messageId)
    }
  }

  const send = (messageId: string) => {
    // Świadomie nie awaitujemy — wysyłka biegnie w tle. Ale `trySendout` ma
    // try/finally BEZ catch, więc bez tego uchwytu każdy rzut w środku wychodzi
    // jako unhandled rejection: w testach ląduje po zwinięciu środowiska, gdzie
    // vitest nie umie go przypisać do żadnego testu, i wywraca cały build mimo
    // że wszystkie testy przechodzą. Pojedynczy nieudany sendout nie jest tego
    // wart.
    void trySendout(messageId).catch((error) => {
      console.warn(
        `[SendoutQueueWorker] Sendout crashed for ${messageId}`,
        error,
      )
    })
  }

  return { send }
}

export default SendoutQueueWorker
export type { SendoutQueueWorkerType as SendoutQueueWorker }
